/**
 * Zod schemas for feedback_reports DB shape + client submission payload.
 *
 * Spec authority: `.plan/features/2026-04-24-admin-beta-feedback.md §4.2 + §4.3`
 * Architecture ref: `architecture.md §4.7` (D1 global schema additions, §4.29).
 *
 * Pure TS + Zod — no fs / node / DOM imports (package purity invariant).
 */

import { z } from "zod";
import { UUID_RE } from "../urls/types.js";

const uuid = z.string().regex(UUID_RE);
const timestamp = z.number().int().nonnegative();

// ── FeedbackKind ──────────────────────────────────────────────────────────────

export const FeedbackKindSchema = z.enum(["bug", "feedback"]);
export type FeedbackKind = z.infer<typeof FeedbackKindSchema>;

// ── FeedbackDispatchStatus ────────────────────────────────────────────────────

export const FeedbackDispatchStatusSchema = z.enum([
  "pending",
  "dispatched",
  "failed",
  "abandoned",
]);
export type FeedbackDispatchStatus = z.infer<typeof FeedbackDispatchStatusSchema>;

// ── FeedbackReport (DB row shape) ─────────────────────────────────────────────

export const FeedbackReportSchema = z
  .object({
    uuid,
    user_uuid: uuid,
    kind: FeedbackKindSchema,
    body: z.string(),
    metadata_json: z.string(), // { url, ua, app_version, viewport, locale }
    screenshot_r2_key: z.string().nullable().optional(),
    gh_issue_number: z.number().int().nonnegative().nullable().optional(),
    gh_dispatch_status: FeedbackDispatchStatusSchema,
    gh_dispatch_error: z.string().nullable().optional(),
    gh_dispatch_attempts: z.number().int().nonnegative(),
    created_at: timestamp,
    dispatched_at: timestamp.nullable().optional(),
  })
  .strict();

export type FeedbackReport = z.infer<typeof FeedbackReportSchema>;

// ── FeedbackSubmitRequest (client → API payload) ──────────────────────────────
// body capped at 4096 chars; metadata whitelisted to known keys.

export const FeedbackMetadataSchema = z
  .object({
    url: z.string(),
    ua: z.string(),
    app_version: z.string(),
    viewport: z.string(),
    locale: z.string(),
  })
  .strict();

export type FeedbackMetadata = z.infer<typeof FeedbackMetadataSchema>;

export const FeedbackSubmitRequestSchema = z
  .object({
    kind: FeedbackKindSchema,
    body: z.string().min(1).max(4096),
    metadata: FeedbackMetadataSchema,
    screenshotR2Key: z.string().nullable().optional(),
  })
  .strict();

export type FeedbackSubmitRequest = z.infer<typeof FeedbackSubmitRequestSchema>;
